import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { ArrowLeft, Users, BookOpen, TrendingUp } from "lucide-react";
import { DetailPageSkeleton } from "@/components/skeletons/PageSkeletons";

function pctColor(pct) {
  if (pct >= 90) return "text-[#059669]";
  if (pct >= 75) return "text-[#C8102E]";
  if (pct >= 60) return "text-[#D97706]";
  return "text-[#E11D48]";
}
function pctBar(pct) {
  if (pct >= 90) return "bg-[#059669]";
  if (pct >= 75) return "bg-[#C8102E]";
  if (pct >= 60) return "bg-[#D97706]";
  return "bg-[#E11D48]";
}

export default function SectionDetail() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const navigate = useNavigate();

  useEffect(() => { api.get(`/sections/${id}`).then(r => setData(r.data)); }, [id]);
  if (!data) return <DetailPageSkeleton testId="section-loading" />;

  return (
    <div className="min-h-screen">
      <div className="px-6 md:px-12 pt-10 pb-6">
        <button onClick={() => navigate(-1)} className="flex items-center gap-1 text-sm text-[#475569] hover:text-[#0F172A] mb-4" data-testid="back-btn">
          <ArrowLeft className="w-4 h-4" /> Back
        </button>
        <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#94A3B8]">Student group</div>
        <h1 className="font-heading text-4xl sm:text-5xl font-extrabold tracking-tighter text-[#0F172A] mt-2" data-testid="section-heading">Section {data.section.name}</h1>
      </div>

      <div className="grid grid-cols-3 border-t border-l border-[#E2E8F0] bg-white">
        <div className="border-r border-b border-[#E2E8F0] p-6">
          <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-[#94A3B8]"><TrendingUp className="w-3.5 h-3.5" /> Overall</div>
          <div className={`font-heading text-3xl font-extrabold tracking-tighter mt-2 ${pctColor(data.overall_pct)}`}>{data.overall_pct}%</div>
        </div>
        <div className="border-r border-b border-[#E2E8F0] p-6">
          <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-[#94A3B8]"><Users className="w-3.5 h-3.5" /> Students</div>
          <div className="font-heading text-3xl font-extrabold tracking-tighter text-[#0F172A] mt-2">{data.students.length}</div>
        </div>
        <div className="border-r border-b border-[#E2E8F0] p-6">
          <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-[#94A3B8]"><BookOpen className="w-3.5 h-3.5" /> Classes</div>
          <div className="font-heading text-3xl font-extrabold tracking-tighter text-[#0F172A] mt-2">{data.classes.length}</div>
        </div>
      </div>

      {/* Classes taught to this section */}
      <div className="px-6 md:px-12 py-10">
        <h2 className="font-heading text-2xl font-bold tracking-tight text-[#0F172A] mb-4">Classes for this section</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="section-classes">
          {data.classes.map(c => (
            <button
              key={c.id}
              onClick={() => navigate(`/classes/${c.id}`)}
              className="text-left bg-white border border-[#E2E8F0] rounded-lg p-5 hover:border-[#C8102E] transition-colors duration-200 ease-in-out"
              data-testid={`section-class-${c.id}`}
            >
              <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#94A3B8]">{c.course_code}</div>
              <div className="font-heading text-lg font-bold tracking-tight text-[#0F172A] mt-1">{c.course_name}</div>
              <div className={`mt-3 text-sm font-heading font-bold ${pctColor(c.attendance_pct)}`}>{c.attendance_pct}% attendance</div>
            </button>
          ))}
        </div>
      </div>

      {/* Students */}
      <div className="px-6 md:px-12 pb-16">
        <h2 className="font-heading text-2xl font-bold tracking-tight text-[#0F172A] mb-4">Roster</h2>
        <div className="bg-white border border-[#E2E8F0] rounded-lg overflow-hidden">
          {data.students.map((s, i) => (
            <button
              key={s.id}
              onClick={() => navigate(`/students/${s.id}`)}
              className={`w-full text-left px-6 py-4 flex items-center gap-4 hover:bg-[#F1F5F9] transition-colors duration-200 ease-in-out ${i > 0 ? "border-t border-[#E2E8F0]" : ""}`}
              data-testid={`section-student-${s.id}`}
            >
              <div className="w-9 h-9 rounded-full bg-[#FDECEF] text-[#C8102E] flex items-center justify-center font-bold text-xs shrink-0">
                {s.name.split(" ").map(x => x[0]).slice(0,2).join("")}
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-[#0F172A]">{s.name}</div>
                <div className="text-xs text-[#94A3B8]">Roll {s.roll_no}</div>
              </div>
              <div className="hidden md:block w-40">
                <div className="h-2 bg-[#F1F5F9] rounded-full overflow-hidden">
                  <div className={`h-full ${pctBar(s.attendance_pct)}`} style={{ width: `${s.attendance_pct}%` }} />
                </div>
              </div>
              <div className={`w-14 text-right font-heading font-bold ${pctColor(s.attendance_pct)}`}>{s.attendance_pct}%</div>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
